import axios from 'axios';
import isGridItemEquipped from './isGridItemEquipped';
import {
  GRID_SIZE,
  GRID_PAGE_SIZE,
  INVENTORY_PAGE_COUNT,
  getItemGridSize,
  getFootprintIndexes,
  computePageCoverage,
  isSlotLocked,
} from './inventoryBagGridUtils';

export const INVENTORY_TYPE_CONFIG = [
  { inventoryType: 'weapons', slotType: 'weapon', labelKey: 'slotWeapon' },
  { inventoryType: 'armors', slotType: 'armor', labelKey: 'slotArmor' },
  { inventoryType: 'helmets', slotType: 'helmet', labelKey: 'slotHelmet' },
  { inventoryType: 'boots', slotType: 'boot', labelKey: 'slotBoot' },
  { inventoryType: 'earrings', slotType: 'earring', labelKey: 'slotEarring' },
  { inventoryType: 'necklaces', slotType: 'necklace', labelKey: 'slotNecklace' },
  { inventoryType: 'shields', slotType: 'shield', labelKey: 'slotShield' },
  { inventoryType: 'pots', slotType: 'pot', labelKey: 'slotPot' },
  { inventoryType: 'others', slotType: 'other', labelKey: 'slotOther' },
];

export const DEFAULT_UNLOCKED_SLOTS = 60;

export const ENHANCEABLE_SLOT_TYPES = new Set([
  'weapon',
  'armor',
  'helmet',
  'boot',
  'shield',
  'earring',
  'necklace',
]);

export const getBaseItemId = (invItem) => (
  invItem.weaponId
  || invItem.armorId
  || invItem.helmetId
  || invItem.bootId
  || invItem.earringId
  || invItem.necklaceId
  || invItem.shieldId
  || invItem.potId
  || invItem.otherId
);

export const normalizeUnlockedCount = (value) => Math.min(
  GRID_SIZE,
  Math.max(DEFAULT_UNLOCKED_SLOTS, Number(value) || DEFAULT_UNLOCKED_SLOTS)
);

export const mergeInventoryFromBootstrap = (bootstrapData, currentEquipment, t) => (
  INVENTORY_TYPE_CONFIG.flatMap((config) => {
    const userItems = bootstrapData?.inventory?.[config.inventoryType] || [];
    const baseItems = bootstrapData?.catalog?.[config.inventoryType] || [];
    return userItems.map((invItem) => {
      const baseItemId = getBaseItemId(invItem);
      const baseItem = baseItems.find((item) => item.id === baseItemId);
      if (!baseItem) return null;
      return {
        ...invItem,
        uniqueKey: `${config.inventoryType}-${invItem.id}`,
        inventoryType: config.inventoryType,
        slotType: config.slotType,
        categoryLabel: t(config.labelKey),
        baseItem,
        count: invItem.count || 1,
      };
    }).filter(Boolean);
  }).filter((item) => !isGridItemEquipped(item, currentEquipment))
);

const computeGridCoverage = (gridSlots) => {
  const coveredIndexes = new Set();
  const spanByIndex = {};
  for (let pageIndex = 0; pageIndex < INVENTORY_PAGE_COUNT; pageIndex += 1) {
    const pageStartIndex = pageIndex * GRID_PAGE_SIZE;
    const pageSlots = gridSlots.slice(pageStartIndex, pageStartIndex + GRID_PAGE_SIZE);
    const pageCoverage = computePageCoverage(pageSlots, pageStartIndex);
    Object.entries(pageCoverage.spanByIndex).forEach(([idx, span]) => {
      spanByIndex[Number(idx)] = span;
    });
    pageCoverage.coveredIndexes.forEach((idx) => coveredIndexes.add(idx));
  }
  return { coveredIndexes, spanByIndex };
};

export const canPlaceItemInGrid = ({
  gridSlots,
  sourceIndex = null,
  targetIndex,
  movingItem,
  unlockedCount = DEFAULT_UNLOCKED_SLOTS,
}) => {
  if (targetIndex === null || targetIndex === undefined) {
    return { ok: false, reason: 'invalid-target' };
  }
  if (!movingItem) {
    return { ok: false, reason: 'missing-item' };
  }

  const { coveredIndexes, spanByIndex } = computeGridCoverage(gridSlots);
  const moveSize = getItemGridSize(movingItem);
  const targetFootprint = getFootprintIndexes(targetIndex, moveSize.width, moveSize.height);
  if (!targetFootprint) {
    return { ok: false, reason: 'out-of-bounds' };
  }

  let sourceFootprint = new Set();
  if (sourceIndex !== null) {
    const sourceSpan = spanByIndex[sourceIndex] || { width: 1, height: 1 };
    const sourceIndexes = getFootprintIndexes(sourceIndex, sourceSpan.width, sourceSpan.height) || [sourceIndex];
    sourceFootprint = new Set(sourceIndexes);
  }

  for (const checkIndex of targetFootprint) {
    if (isSlotLocked(checkIndex, unlockedCount)) {
      return { ok: false, reason: 'locked' };
    }
    if (sourceFootprint.has(checkIndex)) continue;
    if (gridSlots[checkIndex]) {
      return { ok: false, reason: 'occupied' };
    }
    if (coveredIndexes.has(checkIndex)) {
      return { ok: false, reason: 'occupied' };
    }
  }
  return { ok: true };
};

const placeItemInFirstAvailableSlot = (grid, item, preferredIndex, unlockedCount) => {
  if (preferredIndex !== null) {
    const preferredResult = canPlaceItemInGrid({
      gridSlots: grid,
      targetIndex: preferredIndex,
      movingItem: item,
      unlockedCount,
    });
    if (preferredResult.ok) {
      const next = [...grid];
      next[preferredIndex] = item;
      return { grid: next, placed: true };
    }
  }
  for (let index = 0; index < unlockedCount; index += 1) {
    const result = canPlaceItemInGrid({
      gridSlots: grid,
      targetIndex: index,
      movingItem: item,
      unlockedCount,
    });
    if (result.ok) {
      const next = [...grid];
      next[index] = item;
      return { grid: next, placed: true };
    }
  }
  return { grid, placed: false };
};

export const applySavedLayout = async ({
  username,
  mergedItems,
  layoutData = null,
  unlockedSlotsCount = DEFAULT_UNLOCKED_SLOTS,
}) => {
  let activeUnlockedCount = unlockedSlotsCount;

  let fallbackGrid = Array(GRID_SIZE).fill(null);
  mergedItems.forEach((item) => {
    const result = placeItemInFirstAvailableSlot(fallbackGrid, item, null, activeUnlockedCount);
    fallbackGrid = result.grid;
  });

  if (!username) {
    return { gridSlots: fallbackGrid, unlockedSlotsCount: activeUnlockedCount };
  }

  try {
    let savedSlots = null;
    if (layoutData) {
      if (layoutData.unlockedSlots != null) {
        activeUnlockedCount = normalizeUnlockedCount(layoutData.unlockedSlots);
      }
      savedSlots = Array.isArray(layoutData.slots) ? layoutData.slots : null;
    } else {
      const layoutResponse = await axios.get(`/api/inventory/layout?username=${username}`);
      if (layoutResponse.data?.unlockedSlots != null) {
        activeUnlockedCount = normalizeUnlockedCount(layoutResponse.data.unlockedSlots);
      }
      savedSlots = layoutResponse.data?.success ? layoutResponse.data.slots : null;
    }
    if (!Array.isArray(savedSlots)) {
      return { gridSlots: fallbackGrid, unlockedSlotsCount: activeUnlockedCount };
    }

    const itemMap = new Map(mergedItems.map((item) => [item.uniqueKey, item]));
    const usedKeys = new Set();
    let persistedGrid = Array(GRID_SIZE).fill(null);

    savedSlots.forEach((slot) => {
      const slotIndex = Number(slot?.slotIndex);
      const itemKey = typeof slot?.itemKey === 'string' ? slot.itemKey : '';
      if (!Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex >= GRID_SIZE) return;
      if (isSlotLocked(slotIndex, activeUnlockedCount)) return;
      if (!itemKey) return;
      const item = itemMap.get(itemKey);
      if (!item || usedKeys.has(itemKey)) return;
      const result = placeItemInFirstAvailableSlot(persistedGrid, item, slotIndex, activeUnlockedCount);
      persistedGrid = result.grid;
      if (result.placed) {
        usedKeys.add(itemKey);
      }
    });

    const remainingItems = mergedItems.filter((item) => !usedKeys.has(item.uniqueKey));
    remainingItems.forEach((item) => {
      const result = placeItemInFirstAvailableSlot(persistedGrid, item, null, activeUnlockedCount);
      persistedGrid = result.grid;
    });

    return { gridSlots: persistedGrid, unlockedSlotsCount: activeUnlockedCount };
  } catch (layoutError) {
    return { gridSlots: fallbackGrid, unlockedSlotsCount: activeUnlockedCount };
  }
};

export const buildInventoryGridFromBootstrap = async ({
  username,
  bootstrapData,
  equipment,
  t,
}) => {
  const mergedItems = mergeInventoryFromBootstrap(bootstrapData, equipment, t);
  const initialUnlocked = bootstrapData?.layout?.unlockedSlots != null
    ? normalizeUnlockedCount(bootstrapData.layout.unlockedSlots)
    : DEFAULT_UNLOCKED_SLOTS;
  const { gridSlots, unlockedSlotsCount } = await applySavedLayout({
    username,
    mergedItems,
    layoutData: bootstrapData?.layout || null,
    unlockedSlotsCount: initialUnlocked,
  });
  return { gridSlots, allItems: mergedItems, unlockedSlotsCount };
};

export const buildInventoryGridFromTypeRequests = async ({
  username,
  equipment,
  typeRequests,
}) => {
  const groupedResults = await Promise.all(typeRequests);
  const mergedItems = groupedResults
    .flat()
    .filter((item) => !isGridItemEquipped(item, equipment));
  const { gridSlots, unlockedSlotsCount } = await applySavedLayout({
    username,
    mergedItems,
    layoutData: null,
    unlockedSlotsCount: DEFAULT_UNLOCKED_SLOTS,
  });
  return { gridSlots, allItems: mergedItems, unlockedSlotsCount };
};

export const buildDisplayGridSlotsHidingItem = (gridSlots, uniqueKey) => {
  if (!uniqueKey) return gridSlots;
  const anchorIndex = gridSlots.findIndex((item) => item?.uniqueKey === uniqueKey);
  if (anchorIndex < 0) return gridSlots;

  const movingItem = gridSlots[anchorIndex];
  const { width, height } = getItemGridSize(movingItem);
  const footprint = getFootprintIndexes(anchorIndex, width, height) || [anchorIndex];
  const next = [...gridSlots];
  footprint.forEach((idx) => {
    next[idx] = null;
  });
  return next;
};

export const gridItemToEnhanceItem = (slotItem) => ({
  id: slotItem.id,
  itemType: slotItem.slotType,
  baseItemId: slotItem.baseItem?.id ?? getBaseItemId(slotItem),
  name: slotItem.baseItem?.name,
  imageUrl: slotItem.baseItem?.imageUrl,
  enhancement: slotItem.enhancement ?? slotItem.baseItem?.enhancement ?? 0,
  level: slotItem.level ?? slotItem.baseItem?.level ?? 1,
  uniqueKey: slotItem.uniqueKey,
});

export const isEnhanceableGridItem = (slotItem) => (
  !!slotItem && ENHANCEABLE_SLOT_TYPES.has(slotItem.slotType)
);
